// Reading a GaoSafe proposal's approval state.
//
// This is a typed client for a route the console already calls — the
// Approvals screen loads the same family — and NOT a second approval engine.
// It creates nothing, approves nothing and computes no threshold. The only
// question it asks is: what does the server say about this proposal right now?
//
// WHY A SEPARATE MODULE AND NOT AN IMPORT FROM `pages/Approvals.tsx`. That
// page declares its response types inline, as a page's private detail. A
// second surface needing the same read is what turns a page's private shape
// into a module — importing types out of a page component would couple the
// Control Plane's execute step to a screen it has nothing else to do with.
//
// THE STATUS IS THE SERVER'S. Nothing here infers `approved` from a count, an
// elapsed time, or the operator having clicked approve on another screen. The
// server lazily expires a proposal on this exact read, so a proposal past its
// TTL comes back `expired` rather than `proposed` — which is why the console
// asks again instead of tracking the clock itself.

import { apiCall } from "./client";

/** The proposal lifecycle, exactly as `admin_owner_action_proposals.status`
 *  records it. */
export type ProposalStatus =
  | "proposed"
  | "approved"
  | "rejected"
  | "expired"
  | "executed"
  | "cancelled";

export interface ProposalView {
  id: string;
  actionType: string;
  actionDigest: string;
  payloadHash: string;
  thresholdRequired: number;
  status: ProposalStatus;
  proposedByOwnerId: string;
  expiresAt: string;
  createdAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  executedAt: string | null;
  cancelledAt: string | null;
  /** Deduplicated by owner, server-side. */
  approveCount: number;
  rejectCount: number;
  executedTxHash: string | null;
  executedBlockNumber: number | null;
  executedByWalletAddress: string | null;
}

export interface ApprovalDecisionView {
  id: string;
  ownerIdentityId: string;
  walletAddress: string;
  decision: "approve" | "reject";
  signedAt: string | null;
  createdAt: string;
}

export interface ProposalDetailResponse {
  ok: true;
  proposal: ProposalView;
  /** The redacted payload the proposal committed to. For a Control Plane
   *  proposal this is `{domain, operation, productId, lane, target}`. */
  payload: unknown;
  approvals: ApprovalDecisionView[];
  /** SERVER-DERIVED. The console renders this; it never recomputes it from
   *  `approveCount >= thresholdRequired`, because a veto class can meet the
   *  count and still be refused. */
  thresholdMet: boolean;
  vetoed: boolean;
  sessionOwnerIdentityId: string;
  sessionWalletAddress: string;
  sessionHasDecided: boolean;
  sessionOwnerDecision: "approve" | "reject" | null;
  requestId: string;
}

/**
 * Read one proposal.
 *
 * `resourceNotFound` is opted into for the same reason the Control Plane
 * family opts in: `404 {error:"not_found", message:"proposal not found"}` is an
 * ANSWER — the id does not exist on this tier — and reporting it as a routing
 * regression would send an operator to look at deployments instead of at the
 * id they pasted.
 */
export function getProposal(proposalId: string): Promise<ProposalDetailResponse> {
  return apiCall<ProposalDetailResponse>(
    `/v2/admin/bff/approvals/${encodeURIComponent(proposalId)}`,
    { resourceNotFound: true },
  );
}

/** Terminal states: asking again will never change the answer. */
export function isTerminal(status: ProposalStatus): boolean {
  return (
    status === "rejected" ||
    status === "expired" ||
    status === "executed" ||
    status === "cancelled"
  );
}

/** One word for the operator, and the reason it is that word.
 *
 *  `approved` is reported from the server's own `status`, never from
 *  `approveCount >= thresholdRequired`. Those disagree exactly when it matters
 *  most: a veto-class proposal with the count met and one rejection recorded
 *  is NOT approved, and a console that did the arithmetic itself would draw a
 *  green light over a vetoed action. */
export function describeProposal(d: ProposalDetailResponse): {
  status: ProposalStatus;
  label: string;
  detail: string;
  /** True only when the server says this proposal may be spent now. */
  executable: boolean;
} {
  const p = d.proposal;
  const progress = `${p.approveCount} of ${p.thresholdRequired} approvals`;
  switch (p.status) {
    case "proposed":
      return {
        status: p.status,
        label: "PENDING",
        detail: d.vetoed
          ? `${progress} — a rejection has been recorded.`
          : `${progress}. It cannot be executed until the server reports it approved.`,
        executable: false,
      };
    case "approved":
      return {
        status: p.status,
        label: "APPROVED",
        detail: `${progress}. Approved${p.approvedAt ? ` at ${p.approvedAt}` : ""}; expires ${p.expiresAt}.`,
        executable: true,
      };
    case "rejected":
      return {
        status: p.status,
        label: "REJECTED",
        detail: `A signer rejected this proposal${p.rejectedAt ? ` at ${p.rejectedAt}` : ""}. It cannot be approved afterwards.`,
        executable: false,
      };
    case "expired":
      return {
        status: p.status,
        label: "EXPIRED",
        detail: `The proposal passed its expiry (${p.expiresAt}) before it was executed.`,
        executable: false,
      };
    case "executed":
      return {
        status: p.status,
        label: "CONSUMED",
        detail: `Already executed${p.executedAt ? ` at ${p.executedAt}` : ""}. A proposal is spent once; this one is finished.`,
        executable: false,
      };
    case "cancelled":
      return {
        status: p.status,
        label: "CANCELLED",
        detail: `Cancelled${p.cancelledAt ? ` at ${p.cancelledAt}` : ""}.`,
        executable: false,
      };
  }
}
